import React, { useState, useEffect, useRef } from 'react';
import { FiCamera, FiTrash2, FiStar, FiUpload, FiAlertCircle, FiX } from 'react-icons/fi';
import { productosService } from '../../services';
import './ProductImageGallery.scss';

/**
 * Componente para gestión de múltiples imágenes de producto
 * Soporta hasta 10 imágenes con metadatos, reordenamiento y marcado de imagen principal
 * Cuando no hay productId, permite seleccionar imágenes pendientes que se subirán al guardar
 */
const ProductImageGallery = ({ productId, onImagesChange, onPendingFilesChange }) => {
  const [images, setImages] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // archivos locales sin productId aún
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const pendingPreviewUrlsRef = useRef([]);

  const MAX_IMAGES = 10;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

  // Limpiar object URLs al desmontar
  useEffect(() => {
    return () => {
      pendingPreviewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Cargar imágenes existentes cuando hay un productId
  useEffect(() => {
    if (productId) {
      loadImages();
    }
  }, [productId]);

  const loadImages = async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await productosService.getProductImages(productId);

      if (response.success) {
        setImages(response.data || []);
        if (onImagesChange) {
          onImagesChange(response.data || []);
        }
      } else {
        setError(response.error || 'Error al cargar las imágenes');
      }
    } catch (err) {
      console.error('Error cargando imágenes:', err);
      setError('Error al cargar las imágenes del producto');
    } finally {
      setLoading(false);
    }
  };

  const validateFiles = (files, currentCount) => {
    if (currentCount + files.length > MAX_IMAGES) {
      return `Máximo ${MAX_IMAGES} imágenes permitidas. Actualmente tienes ${currentCount}.`;
    }
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        return `Tipo de archivo no permitido: ${file.type}. Use JPG, PNG, WEBP o GIF.`;
      }
      if (file.size > MAX_FILE_SIZE) {
        return `El archivo ${file.name} excede el tamaño máximo de 10MB.`;
      }
    }
    return null;
  };

  // Selección de archivos en modo pendiente (sin productId)
  const handlePendingFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const validationError = validateFiles(files, pendingFiles.length);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    const newPendingFiles = files.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name
    }));
    pendingPreviewUrlsRef.current.push(...newPendingFiles.map(f => f.previewUrl));

    const updated = [...pendingFiles, ...newPendingFiles];
    setPendingFiles(updated);
    if (onPendingFilesChange) onPendingFilesChange(updated.map(pf => pf.file));
    if (onImagesChange) onImagesChange(updated.map(pf => pf.file));
    event.target.value = '';
  };

  const handleRemovePending = (index) => {
    const removed = pendingFiles[index];
    URL.revokeObjectURL(removed.previewUrl);
    pendingPreviewUrlsRef.current = pendingPreviewUrlsRef.current.filter(u => u !== removed.previewUrl);
    const updated = pendingFiles.filter((_, i) => i !== index);
    setPendingFiles(updated);
    if (onPendingFilesChange) onPendingFilesChange(updated.map(pf => pf.file));
    if (onImagesChange) onImagesChange(updated.map(pf => pf.file));
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);

    if (!files.length) return;

    const validationError = validateFiles(files, images.length);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Subir archivos uno por uno
    setUploading(true);
    setError(null);

    for (const file of files) {
      try {
        const isPrimary = images.length === 0; // Primera imagen es principal
        const displayOrder = images.length;

        const response = await productosService.uploadProductImage(productId, file, {
          alt_text: `Imagen de producto`,
          is_primary: isPrimary,
          display_order: displayOrder
        });

        if (response.success) {
          setImages(prev => [...prev, response.data]);
        } else {
          setError(response.error || `Error al subir ${file.name}`);
          break;
        }
      } catch (err) {
        console.error('Error subiendo imagen:', err);
        setError(`Error al subir ${file.name}`);
        break;
      }
    }

    setUploading(false);
    event.target.value = ''; // Reset input

    // Recargar imágenes para obtener el orden actualizado
    await loadImages();
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('¿Está seguro de eliminar esta imagen?')) {
      return;
    }

    setError(null);

    try {
      const response = await productosService.deleteProductImage(productId, imageId);

      if (response.success) {
        setImages(prev => prev.filter(img => img.id !== imageId));
        await loadImages(); // Recargar para actualizar orden
      } else {
        setError(response.error || 'Error al eliminar la imagen');
      }
    } catch (err) {
      console.error('Error eliminando imagen:', err);
      setError('Error al eliminar la imagen');
    }
  };

  const handleSetPrimary = async (imageId) => {
    setError(null);

    try {
      // Actualizar la imagen seleccionada como principal
      const response = await productosService.updateImageMetadata(productId, imageId, {
        is_primary: true
      });

      if (response.success) {
        await loadImages(); // Recargar para reflejar cambios
      } else {
        setError(response.error || 'Error al establecer imagen principal');
      }
    } catch (err) {
      console.error('Error estableciendo imagen principal:', err);
      setError('Error al establecer la imagen principal');
    }
  };

  const getImageUrl = (image) => {
    // Las imágenes se sirven desde la raíz del servidor, sin /api/v1
    // Extraer solo el host base de REACT_APP_API_URL (remover /api/v1)
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    const baseUrl = apiUrl.replace(/\/api\/v1\/?$/, ''); // http://localhost:8000

    // Usar la URL medium para el preview
    if (image.medium_url) {
      return `${baseUrl}${image.medium_url}`;
    }
    if (image.url) {
      return `${baseUrl}${image.url}`;
    }
    return '';
  };

  // Si no hay productId, mostrar modo pendiente (subida diferida)
  if (!productId) {
    return (
      <div className="product-image-gallery">
        <div className="gallery-header">
          <div className="gallery-info">
            <h3 className="gallery-title">Imágenes del Producto</h3>
            <span className="image-count">
              {pendingFiles.length} / {MAX_IMAGES} imágenes
            </span>
          </div>
          {pendingFiles.length < MAX_IMAGES && (
            <label className="upload-button">
              <FiUpload className="upload-icon" />
              <span>Subir Imágenes</span>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                multiple
                onChange={handlePendingFileSelect}
                style={{ display: 'none' }}
              />
            </label>
          )}
        </div>

        {error && (
          <div className="gallery-error">
            <FiAlertCircle className="error-icon" />
            <span>{error}</span>
          </div>
        )}

        {pendingFiles.length > 0 ? (
          <div className="images-grid">
            {pendingFiles.map((pf, index) => (
              <div key={index} className="image-card">
                {index === 0 && (
                  <div className="primary-badge">
                    <FiStar className="star-icon" />
                    <span>Principal</span>
                  </div>
                )}
                <div className="image-preview">
                  <img src={pf.previewUrl} alt={pf.name} />
                </div>
                <div className="image-actions">
                  <button
                    type="button"
                    className="action-button delete"
                    onClick={() => handleRemovePending(index)}
                    title="Quitar imagen"
                  >
                    <FiX />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="gallery-empty">
            <FiCamera className="empty-icon" />
            <p className="empty-text">No hay imágenes todavía</p>
            <p className="empty-hint">Las imágenes se subirán al guardar el producto</p>
          </div>
        )}

        <div className="gallery-help">
          <small>
            Formatos permitidos: JPG, PNG, WEBP, GIF. Tamaño máximo: 10MB por imagen.
            La primera imagen será la principal.
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="product-image-gallery">
      {/* Header con botón de subida */}
      <div className="gallery-header">
        <div className="gallery-info">
          <h3 className="gallery-title">Imágenes del Producto</h3>
          <span className="image-count">
            {images.length} / {MAX_IMAGES} imágenes
          </span>
        </div>

        {images.length < MAX_IMAGES && (
          <label className="upload-button">
            <FiUpload className="upload-icon" />
            <span>Subir Imágenes</span>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              multiple
              onChange={handleFileSelect}
              disabled={uploading || loading}
              style={{ display: 'none' }}
            />
          </label>
        )}
      </div>

      {/* Mensajes de error */}
      {error && (
        <div className="gallery-error">
          <FiAlertCircle className="error-icon" />
          <span>{error}</span>
        </div>
      )}

      {/* Estado de carga */}
      {(loading || uploading) && (
        <div className="gallery-loading">
          <div className="loading-spinner"></div>
          <span>{uploading ? 'Subiendo imágenes...' : 'Cargando...'}</span>
        </div>
      )}

      {/* Grid de imágenes */}
      {!loading && images.length > 0 && (
        <div className="images-grid">
          {images.map((image) => (
            <div key={image.id} className="image-card">
              {/* Badge de imagen principal */}
              {image.is_primary && (
                <div className="primary-badge">
                  <FiStar className="star-icon" />
                  <span>Principal</span>
                </div>
              )}

              {/* Imagen preview */}
              <div className="image-preview">
                <img
                  src={getImageUrl(image)}
                  alt={image.alt_text || 'Producto'}
                />
              </div>

              {/* Acciones */}
              <div className="image-actions">
                {!image.is_primary && (
                  <button
                    type="button"
                    className="action-button set-primary"
                    onClick={() => handleSetPrimary(image.id)}
                    title="Establecer como principal"
                  >
                    <FiStar />
                  </button>
                )}

                <button
                  type="button"
                  className="action-button delete"
                  onClick={() => handleDeleteImage(image.id)}
                  title="Eliminar imagen"
                >
                  <FiTrash2 />
                </button>
              </div>

              {/* Info de la imagen */}
              <div className="image-info">
                <span className="image-size">
                  {image.file_size ? `${(image.file_size / 1024).toFixed(1)} KB` : ''}
                </span>
                {image.width && image.height && (
                  <span className="image-dimensions">
                    {image.width} × {image.height}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Estado vacío */}
      {!loading && images.length === 0 && (
        <div className="gallery-empty">
          <FiCamera className="empty-icon" />
          <p className="empty-text">No hay imágenes todavía</p>
          <p className="empty-hint">
            Haga clic en "Subir Imágenes" para agregar hasta {MAX_IMAGES} imágenes
          </p>
        </div>
      )}

      {/* Ayuda */}
      <div className="gallery-help">
        <small>
          Formatos permitidos: JPG, PNG, WEBP, GIF. Tamaño máximo: 10MB por imagen.
          La primera imagen será la principal por defecto.
        </small>
      </div>
    </div>
  );
};

export default ProductImageGallery;
